import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'
import type { Table } from '@tanstack/react-table'
import { Button } from '@/shared/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

interface DataTablePaginationProps<TData> {
  table: Table<TData>
  totalRows: number
  /**
   * When provided, switches to "unknown total" mode (backend doesn't return page count).
   * true = there are more pages, false = this is the last page.
   */
  hasMore?: boolean
}

export function DataTablePagination<TData>({ table, totalRows, hasMore }: DataTablePaginationProps<TData>) {
  const { t } = useTranslation()
  const { pageIndex, pageSize } = table.getState().pagination
  const unknownTotal = hasMore !== undefined

  // Known-total mode
  const pageCount = table.getPageCount()
  const from = totalRows === 0 ? 0 : pageIndex * pageSize + 1
  const to = Math.min((pageIndex + 1) * pageSize, totalRows)

  // Unknown-total mode
  const canPrev = pageIndex > 0
  const canNext = unknownTotal ? hasMore : table.getCanNextPage()

  return (
    <div className="flex items-center justify-between gap-4 px-1 py-1.5 text-xs text-muted-foreground">
      {/* Row count */}
      <span className="shrink-0">
        {unknownTotal
          ? t('dataTable.rowsCount', { count: table.getRowModel().rows.length })
          : totalRows === 0
            ? t('dataTable.noResults')
            : t('dataTable.rowRange', { from, to, total: totalRows.toLocaleString() })}
      </span>

      <div className="flex items-center gap-4">
        {/* Page size selector */}
        <div className="flex items-center gap-1.5">
          <span className="shrink-0">{t('dataTable.rowsPerPage')}</span>
          <Select
            value={String(pageSize)}
            onValueChange={(v) => table.setPageSize(Number(v))}
          >
            <SelectTrigger className="h-6 w-16 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)} className="text-xs">
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Page position */}
        <span className="shrink-0">
          {unknownTotal
            ? t('dataTable.page', { page: pageIndex + 1 })
            : t('dataTable.pageOf', { page: pageCount === 0 ? 0 : pageIndex + 1, total: pageCount })}
        </span>

        {/* Navigation buttons */}
        <div className="flex items-center gap-0.5">
          {!unknownTotal && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
              aria-label={t('dataTable.firstPage')}
            >
              <ChevronsLeft className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => table.previousPage()}
            disabled={unknownTotal ? !canPrev : !table.getCanPreviousPage()}
            aria-label={t('dataTable.previousPage')}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => table.nextPage()}
            disabled={!canNext}
            aria-label={t('dataTable.nextPage')}
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
          {!unknownTotal && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => table.setPageIndex(pageCount - 1)}
              disabled={!table.getCanNextPage()}
              aria-label={t('dataTable.lastPage')}
            >
              <ChevronsRight className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
